import { useEffect, useState } from 'react';
import { Loader2, Save, RefreshCw, AlertTriangle, BookOpen, ExternalLink } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

/**
 * Admin editor for the mandate template. Creating a new version does NOT retroactively
 * update mandates already accepted — those keep their snapshot.
 */
export default function MandateTemplateEditor() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState({ title: '', content: '', version: 1 });
  const [dsStatus, setDsStatus] = useState({ configured: false, consentUrl: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get('/admin/mandate-template'),
        api.get('/admin/docusign/status'),
      ]);
      setTpl(t.data);
      setDsStatus(s.data);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de charger le modèle.', duration: 10000 });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/mandate-template', { title: tpl.title, content: tpl.content });
      setTpl(res.data);
      toast({ title: 'Nouvelle version enregistrée', description: `Modèle v${res.data.version}. Les mandats déjà acceptés ne sont pas affectés.`, duration: 10000 });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible.', duration: 10000 });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement du modèle…</div>;

  return (
    <div className="space-y-6">
      {/* DocuSign status */}
      <div className={`rounded-xl border-2 p-4 flex items-start gap-3 ${dsStatus.configured ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`} data-testid="docusign-status-block">
        {dsStatus.configured ? (
          <>
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <div className="font-bold text-brand-navy">Signature électronique DocuSign — Configurée</div>
              <div className="text-xs text-gray-700 mt-1">
                Environnement : <b>{dsStatus.oauthBase?.includes('account-d') ? 'Sandbox / Développeur' : 'Production'}</b>.
                Si vous n'avez jamais autorisé l'application, ouvrez le lien de consentement ci-dessous <b>une fois</b> depuis un navigateur.
              </div>
              {dsStatus.consentUrl && (
                <a href={dsStatus.consentUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-brand-navy hover:text-brand-yellow text-xs font-bold underline" data-testid="docusign-consent-link">
                  Autoriser l'application (consentement JWT) <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-brand-navy">DocuSign non configuré</div>
              <div className="text-xs text-gray-700 mt-1">Les mandats peuvent quand même être acceptés en <b>signature interne</b> (nom + entreprise + IP + horodatage).</div>
            </div>
          </>
        )}
      </div>

      {/* Template editor */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="mandate-template-editor">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand-navy" />
            <h2 className="font-bold text-brand-navy">Modèle de mandat</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-navy text-white font-bold uppercase tracking-wider">v{tpl.version}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
              <RefreshCw className="w-3.5 h-3.5" /> Recharger
            </button>
            <button data-testid="save-mandate-template-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer une nouvelle version
            </button>
          </div>
        </div>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mb-4">
          <b>Note importante :</b> ce contenu est un brouillon — les clauses juridiques doivent être validées par vos conseillers avant utilisation en production. La création d'une nouvelle version <b>n'affecte pas</b> les mandats déjà acceptés (chacun conserve la version qu'il a signée).
        </div>

        <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">Titre du document</label>
        <input
          data-testid="mandate-template-title"
          value={tpl.title}
          onChange={(e) => setTpl({ ...tpl, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-brand-navy mb-4"
        />

        <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">
          Contenu du mandat (HTML : <code>&lt;h2&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;ul&gt;&lt;li&gt;</code>, <code>&lt;b&gt;</code>)
        </label>
        <textarea
          data-testid="mandate-template-content"
          value={tpl.content}
          onChange={(e) => setTpl({ ...tpl, content: e.target.value })}
          rows={22}
          className="w-full px-3 py-2 border border-gray-200 rounded font-mono text-xs leading-relaxed focus:outline-none focus:border-brand-navy"
        />
      </div>
    </div>
  );
}
